import React, { useCallback, useContext, useEffect, useState } from 'react';
import Icon from '../../common/icons';
import CurrencyContext from '../contexts/CurrencyContext';
import ProductsContext from '../contexts/ProductsContext';
import { getAddedOffers, updateOffer } from '../contexts/products';
import { FullSizeCheckmark } from './elements/AnimatedCheckmark';
import { HTMLSectionWithLink } from './elements/Link';
import Card, { ProductCardSection, Threshold } from './Card';
import ProductCard from './ProductCard';

function firstAvailableVariant(offer, product) {
  if (
    offer.shopify_product_variants.length > 0 &&
    !offer.isAdminProduct &&
    !offer.isAdminOffer
  ) {
    return product.variants.find(
      (v) => v.id === offer.shopify_product_variants[0],
    );
  }
  return product.variants.find((v) => v.available);
}

const ProductCardSectionHOC = ({
  passed,
  offerProduct,
  onClick,
  display_product_names_as_links,
  currency: adminCurrency,
}) => {
  const { isAdminProduct } = offerProduct;

  const currencyCtx = useContext(isAdminProduct ? {} : CurrencyContext);
  const currency = isAdminProduct ? adminCurrency : currencyCtx;

  const productCtx = useContext(isAdminProduct ? {} : ProductsContext);

  const product = isAdminProduct
    ? offerProduct
    : productCtx.getProduct(offerProduct.shopify_product_handle);

  if (!isAdminProduct && (!product || !product.loaded || !product.available)) {
    productCtx.loadProduct(offerProduct);
    return null;
  }

  const variant = firstAvailableVariant(offerProduct, product);
  return (
    <ProductCardSection
      {...{
        currency,
        variant,
        product,
        onClick: () => passed && onClick(product),
        passed,
        display_product_names_as_links,
      }}
    />
  );
};

function useProduct(offer) {
  const [selectedProductVariantId, setSelectedProductVariantId] = useState();

  function selectProduct(product) {
    const product_item = offer.products.find(
      (p) => p.shopify_product_handle === product.handle,
    );
    offer.shopify_product_variants = product_item
      ? product_item.shopify_product_variants
      : [];

    // preselect first selectable
    const variant = firstAvailableVariant(offer, product);
    offer.shopify_product_variant_id = variant.id;
    offer.shopify_product_handle = product.handle;
    // product_item is MultipleProductOfferProduct
    updateOffer(offer, variant, product.handle);
    setSelectedProductVariantId(variant.id);
  }

  const clearSelection = useCallback(() => {
    if (!offer.isAdminOffer) {
      // TODO this was ok, wtf
      offer.shopify_product_variant_id = null;
      offer.shopify_product_handle = null;
      offer.shopify_product_variants = [];
      updateOffer(offer, null);
    }
    setSelectedProductVariantId(null);
  }, [offer]);

  useEffect(() => {
    const addedOffer = getAddedOffers().find((o) => o.offerId === offer.id);
    if (addedOffer && !offer.isAdminOffer) {
      offer.shopify_product_variant_id = addedOffer.productVariantId;
      offer.shopify_product_handle = addedOffer.productHandle;
      const product = offer.products.find(
        (p) => p.shopify_product_handle === addedOffer.productHandle,
      );
      offer.shopify_product_variants = product
        ? product.shopify_product_variants
        : [];
      setSelectedProductVariantId(addedOffer.productVariantId);
    } else {
      // When in preview, we need to clear selection when offer changes.
      clearSelection();
    }
  }, [offer, clearSelection]);

  return {
    selectedProductVariantId,
    selectProduct,
    clearSelection,
  };
}

export default ({ offer }) => {
  const {
    selectedProductVariantId,
    selectProduct,
    clearSelection,
  } = useProduct(offer);

  if (selectedProductVariantId) {
    if (offer.isAdminOffer) {
      const adminProduct = offer.products.find((product) => {
        return product.variants.find(
          (variant) => variant.id === selectedProductVariantId,
        );
      });
      return (
        <ProductCard
          offer={offer}
          adminProduct={adminProduct}
          onEdit={clearSelection}
        />
      );
    }
    return <ProductCard offer={offer} onEdit={clearSelection} />;
  }

  return (
    <Threshold offer={offer}>
      {({ passed }) => (
        <Card
          isMultipleProduct
          offer={offer}
          image={passed ? <FullSizeCheckmark animate /> : <Icon name="gift" />}
          baseContent={offer.products.map((product) => (
            <ProductCardSectionHOC
              key={product.shopify_product_id}
              offerProduct={product}
              passed={passed}
              display_product_names_as_links={
                offer.display_product_names_as_links
              }
              onClick={selectProduct}
              currency={offer.currency}
            />
          ))}
        >
          {!passed && (
            <HTMLSectionWithLink>
              {offer.subtitle_under_threshold ||
                'and select one of the free gifts below'}
            </HTMLSectionWithLink>
          )}
        </Card>
      )}
    </Threshold>
  );
};
